import React, { Fragment } from 'react';

import classNames from 'classnames';

import { categoryKey } from './categories';
import RateCardRate from './RateCardRate';

import { formatFigure } from 'Helpers/formatService';
import { MARGIN_NO_DIVISOR } from 'Helpers/marginPercentage';

import Badge from 'CommonComponents/Badge';
import RowActionsMenu from 'CommonComponents/RowActionsMenu';

import RestoreIcon from 'Assets/icons/arrow-uturn-left.svg';
import ArchiveIcon from 'Assets/icons/box.svg';
import EditIcon from 'Assets/icons/edit.svg';

const TYPE_BADGES = {
    internal: { variant: 'neutral', label: 'Internal' },
    freelance: { variant: 'info', label: 'Freelance' },
};

const STATUS_BADGES = {
    active: { variant: 'success', label: 'Active' },
    archived: { variant: 'neutral', label: 'Archived' },
};

/**
 * Roles arrive ordered by category then name, so the banner breaks fall out of
 * the order rather than needing a second sort. Matching is case-insensitive by
 * the shared rule in `categories.js` — two spellings would otherwise open two
 * groups carrying the same React key.
 */
function groupByCategory(roles) {
    const groups = [];

    roles.forEach((role) => {
        const key = categoryKey(role.category);
        const last = groups[groups.length - 1];

        if (last && last.key === key) {
            last.roles.push(role);

            return;
        }

        groups.push({ key, category: role.category, roles: [role] });
    });

    return groups;
}

/**
 * How many budget lines are booked against an item. A dash covers both no lines
 * and no count in the payload: the resource omits the key rather than sending a
 * zero it cannot vouch for, and either way there is no number to show here.
 */
function inUseLabel(count) {
    if (!count) return '–';

    return count === 1 ? $t('1 line') : $t('{{count}} lines', { count });
}

/**
 * A margin or mark-up at the one decimal place the server rounds it to. A dash
 * where the payload has no figure: no billed rate leaves no margin, and no cost
 * rate no mark-up.
 */
function percentLabel(figure) {
    if (figure === null || figure === undefined) return MARGIN_NO_DIVISOR;

    return formatFigure(figure, (value, precision) => Number(value).toFixed(precision), { precision: 1, suffix: '%' });
}

function RateCardRoleRow({ role, currency, onEdit, onArchive, onRestore }) {
    const type = TYPE_BADGES[role.default_type] || TYPE_BADGES.internal;
    const status = STATUS_BADGES[role.status] || STATUS_BADGES.active;
    const isArchived = role.status === 'archived';

    const actions = [
        {
            key: 'edit',
            label: $t('Edit'),
            icon: <EditIcon className="tw-h-3 tw-w-3" />,
            onClick: () => onEdit(role),
        },
        // Nothing here deletes. A role is retired by archiving it, and comes back
        // the same way — a Delete item that quietly archived would mislead
        // whoever tested it.
        isArchived
            ? {
                  key: 'restore',
                  label: $t('Restore'),
                  icon: <RestoreIcon className="tw-h-3 tw-w-3 tw-text-charcoal-75" />,
                  onClick: () => onRestore(role),
              }
            : {
                  key: 'archive',
                  label: $t('Archive'),
                  icon: <ArchiveIcon className="tw-h-3 tw-w-3 tw-text-charcoal-75" />,
                  onClick: () => onArchive(role),
              },
    ];

    return (
        <tr className={classNames({ 'tw-text-charcoal-50': isArchived })}>
            <td className="tw-font-semibold">{role.name}</td>
            {/* Free text the admin typed. An older role has none — an em dash reads
                as "not said", where a defaulted "Day" would state something nobody
                chose and an expense line would then inherit it. */}
            <td>{role.unit || '—'}</td>
            <td className="inbud-numeric-cell">
                <RateCardRate value={role.cost_rate} currency={currency} />
            </td>
            <td className="inbud-numeric-cell">
                <RateCardRate value={role.billed_rate} currency={currency} />
            </td>
            <td className="inbud-numeric-cell">{percentLabel(role.margin)}</td>
            <td className="inbud-numeric-cell">{percentLabel(role.markup)}</td>
            <td>
                <Badge variant={type.variant}>{$t(type.label)}</Badge>
            </td>
            <td>
                <Badge variant={status.variant}>{$t(status.label)}</Badge>
            </td>
            {/* Why the row offers Archive and never Delete: budget lines are booked
                against this item, and each keeps its own snapshot of the rate, so
                retiring it costs them nothing. */}
            <td className="inbud-numeric-cell">{inUseLabel(role.in_use_count)}</td>
            <td className="tw-w-8">
                <div className="tw-flex tw-justify-center">
                    <RowActionsMenu actions={actions} />
                </div>
            </td>
        </tr>
    );
}

export default function RateCardRolesTable({ roles, currency, onEdit, onArchive, onRestore }) {
    const groups = groupByCategory(roles);

    return (
        <>
            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th className="tw-text-start">{$t('Role')}</th>
                            <th className="tw-text-start">{$t('Unit')}</th>
                            <th className="tw-text-end">{$t('Cost rate')}</th>
                            <th className="tw-text-end">{$t('Billed rate')}</th>
                            <th className="tw-text-end">Margin %</th>
                            <th className="tw-text-end">Mark-up %</th>
                            <th className="tw-text-start">{$t('Default type')}</th>
                            <th className="tw-text-start">{$t('Status')}</th>
                            <th className="tw-text-end">{$t('In use')}</th>
                            <th>
                                <span className="tw-sr-only">{$t('Actions')}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.length === 0 && (
                            <tr>
                                <td colSpan={10} className="tw-text-center tw-text-charcoal-75">
                                    {$t('No roles on this version yet. Add the first one to start pricing time.')}
                                </td>
                            </tr>
                        )}
                        {groups.map((group) => (
                            <Fragment key={group.key}>
                                <tr>
                                    {/* The row rule paints every body row, so the banner needs
                                        the important flag to sit lighter than its rows. */}
                                    <td colSpan={10} className="table__subheader !tw-bg-charcoal-10">
                                        {group.category}
                                    </td>
                                </tr>
                                {group.roles.map((role) => (
                                    <RateCardRoleRow
                                        key={role.id}
                                        role={role}
                                        currency={currency}
                                        onEdit={onEdit}
                                        onArchive={onArchive}
                                        onRestore={onRestore}
                                    />
                                ))}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="tw-mt-4 tw-text-sm tw-text-charcoal-75">
                <p className="tw-font-semibold tw-text-charcoal tw-mb-1">Margin % and Mark-up %</p>
                <p className="tw-mb-1">
                    Margin % is the profit on a role as a share of its billed rate: the difference between billed rate and cost rate, divided by the billed
                    rate. It is the figure used on time reports and the project Margin % tile.
                </p>
                <p className="tw-mb-1">
                    Mark-up % is the same profit expressed as a share of the cost rate: the difference between billed rate and cost rate, divided by the cost
                    rate. It is the figure populated as M/U % when a role is added to a budget line, and remains editable on the line.
                </p>
                <p className="tw-mb-0">
                    Example: a role costing 800 and billed at 1,000 carries a 20% margin and a 25% mark-up. Later changes to the rate card do not update
                    existing budget lines.
                </p>
            </div>
        </>
    );
}
